import { useCallback, useState } from 'react';
import { dealForecastSchema, type DealForecast, type ForecastCategory } from '@forecastify/shared';
import { ListToolbar, Segmented, Skeleton, Tooltip, cn } from '@forecastify/ui';
import { useI18n, useT } from '../../i18n';
import { useLiveInvalidation } from '../../live/use-live';
import { DEAL_TOPICS } from '../../live/topics';
import { useSession } from '../../session';
import { ScreenState, failureOf, type ScreenFailure } from '../screen-state';
import { useFirstLoad } from '../use-first-load';
import { shortMonth } from './board-format';
import { dealModeToolbar } from './board-toolbar';
import { FORECAST_COLORS, dealMoneyShort, forecastLabel } from './deal-kit';
import type { DealModeProps } from './deals-modes';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 20 Oct 2026 · Phase 6 · D7 · *Dealek — Forecast* · `phase6/03`.            │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * Four tiles — the quarter's target, the open pipeline, the coverage, the committed weighted
 * value — and the matrix: forecast category × the next six months, the contracted revenue under
 * it and *Terv + várható* at the bottom. Every figure is the server's (`deal-insights.service.ts`
 * `forecast`); the *Súlyozott / Nyers* switch only chooses which of the two it sent is the big one.
 *
 * ⚠️ **One sum is drawn here rather than read**: the raw *Terv + várható*. The server sends the
 * weighted line only (`expected`); with *Nyers* chosen it is the contracted line plus the raw
 * rows, the omitted row excluded — the same rule the server applies to the weighted one.
 */

type T = ReturnType<typeof useT>;
type Figure = 'weighted' | 'raw';

/** The industry rule of thumb the mockup's note quotes: below 3× is thin. */
const HEALTHY_COVERAGE = 3;

function categoryDescription(t: T, category: ForecastCategory): string {
  if (category === 'commit') return t('dealForecast.desc.commit');
  if (category === 'best_case') return t('dealForecast.desc.best_case');
  if (category === 'omitted') return t('dealForecast.desc.omitted');
  return t('dealForecast.desc.pipeline');
}

export function DealForecastScreen(props: DealModeProps) {
  const t = useT();
  const { locale } = useI18n();
  const session = useSession();
  const [forecast, setForecast] = useState<DealForecast | null>(null);
  const [failure, setFailure] = useState<ScreenFailure | null>(null);
  const [figure, setFigure] = useState<Figure>('weighted');
  const [search, setSearch] = useState('');

  const reload = useCallback(async (): Promise<void> => {
    if (session.workspaceId === null) {
      setFailure({ kind: 'no-workspace' });
      return;
    }
    const response = await session.request('/deals/forecast').catch(() => null);
    if (response === null) {
      setFailure({ kind: 'error', code: 'unavailable' });
      return;
    }
    if (!response.ok) {
      setFailure(await failureOf(response));
      return;
    }
    const parsed = dealForecastSchema.safeParse(await response.json());
    if (!parsed.success) {
      setFailure({ kind: 'error', code: 'validation_failed' });
      return;
    }
    setFailure(null);
    setForecast(parsed.data);
  }, [session]);

  const loading = useFirstLoad(reload);
  useLiveInvalidation(DEAL_TOPICS, reload);

  /* ⚠️ The search narrows the four category rows by their name — the matrix is a fixed aggregate,
     not a list, and the server takes no filter for it. */
  const needle = search.trim().toLocaleLowerCase(locale);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <ListToolbar {...dealModeToolbar(t, props)} search={search} onSearchChange={setSearch} />
      <ScreenState failure={failure} onRetry={() => void reload()}>
        {loading || forecast === null ? (
          <ForecastSkeleton />
        ) : (
          <ForecastBody
            t={t}
            locale={locale}
            forecast={forecast}
            figure={figure}
            onFigure={setFigure}
            needle={needle}
          />
        )}
      </ScreenState>
    </div>
  );
}

function ForecastBody({
  t,
  locale,
  forecast,
  figure,
  onFigure,
  needle,
}: {
  readonly t: T;
  readonly locale: string;
  readonly forecast: DealForecast;
  readonly figure: Figure;
  readonly onFigure: (figure: Figure) => void;
  readonly needle: string;
}) {
  const money = (value: string | number): string => dealMoneyShort(value, forecast.currencyCode);
  const weighted = figure === 'weighted';
  const coverage = forecast.coverage;
  const thin = coverage !== null && coverage < HEALTHY_COVERAGE;
  const rows = forecast.rows.filter(
    (row) =>
      needle === '' || forecastLabel(t, row.category).toLocaleLowerCase(locale).includes(needle),
  );
  const expected = weighted
    ? forecast.expected.map(Number)
    : forecast.contracted.map(
        (contracted, index) =>
          Number(contracted) +
          forecast.rows
            .filter((row) => row.category !== 'omitted')
            .reduce((sum, row) => sum + Number(row.raw[index] ?? 0), 0),
      );

  return (
    <div className="min-h-0 flex-1 overflow-auto p-5">
      <div className="flex flex-wrap items-stretch gap-2">
        <Tile label={t('dealForecast.tile.target', { quarter: forecast.quarter })}>
          {Number(forecast.target) > 0 ? money(forecast.target) : '–'}
        </Tile>
        <Tile label={t('dealForecast.tile.pipeline')}>{money(forecast.pipeline)}</Tile>
        <Tile
          label={t('dealForecast.tile.coverage')}
          className={cn(thin && 'text-status-warn-text')}
        >
          {coverage === null
            ? '–'
            : `${new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(coverage)}×`}
        </Tile>
        <Tile label={t('dealForecast.tile.committed')}>{money(forecast.committedWeighted)}</Tile>
        <div className="bg-card flex min-w-[240px] flex-1 flex-col justify-center gap-1.5 rounded-xl border p-3">
          <p className="text-muted-foreground text-[11.5px] leading-relaxed">
            {coverage === null
              ? t('dealForecast.note.noTarget')
              : thin
                ? t('dealForecast.note.low')
                : t('dealForecast.note.ok')}
          </p>
          <span className="flex items-center gap-2">
            <span className="text-[12px]">{t('dealForecast.figure')}</span>
            <Segmented
              label={t('dealForecast.figure')}
              value={figure}
              onChange={onFigure}
              options={[
                { value: 'weighted', label: t('dealForecast.figure.weighted') },
                { value: 'raw', label: t('dealForecast.figure.raw') },
              ]}
            />
          </span>
        </div>
      </div>

      <div className="bg-card mt-3 overflow-x-auto rounded-xl border">
        <table className="w-full text-[12.5px]">
          <caption className="sr-only">{t('dealForecast.table')}</caption>
          <thead>
            <tr className="text-muted-foreground bg-muted/40 border-b text-[10.5px] font-medium tracking-wide uppercase">
              <th scope="col" className="w-[220px] px-3 py-2 text-left font-medium">
                {t('deals.filter.forecast')}
              </th>
              {forecast.months.map((month) => (
                <th key={month} scope="col" className="px-3 py-2 text-right font-medium">
                  {shortMonth(month, locale)}
                </th>
              ))}
              <th scope="col" className="px-3 py-2 text-right font-medium">
                {t('dealForecast.column.total')}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const omitted = row.category === 'omitted';
              const main = weighted ? row.weighted : row.raw;
              const sub = weighted ? row.raw : row.weighted;
              return (
                <tr
                  key={row.category}
                  className={cn(
                    'hover:bg-muted/30 border-b transition-colors',
                    omitted && 'text-muted-foreground',
                  )}
                >
                  <th scope="row" className="px-3 py-2 text-left font-normal">
                    <span className="flex items-center gap-2">
                      <span
                        aria-hidden
                        className="size-2 shrink-0 rounded-full"
                        style={{ background: FORECAST_COLORS[row.category] }}
                      />
                      <span className="font-medium">{forecastLabel(t, row.category)}</span>
                      <span className="text-muted-foreground text-[11px] tabular-nums">
                        {row.count}
                      </span>
                    </span>
                    <span className="text-muted-foreground mt-0.5 block text-[11px] leading-relaxed">
                      {categoryDescription(t, row.category)}
                    </span>
                  </th>
                  {forecast.months.map((month, index) => {
                    const raw = Number(row.raw[index] ?? 0);
                    return (
                      <td key={month} className="px-3 py-2 text-right">
                        {raw > 0 ? (
                          <>
                            <span className="block whitespace-nowrap tabular-nums">
                              {money(main[index] ?? 0)}
                            </span>
                            <span className="text-muted-foreground block text-[11px] whitespace-nowrap tabular-nums">
                              {money(sub[index] ?? 0)}
                            </span>
                          </>
                        ) : (
                          <span className="text-muted-foreground/60 tabular-nums">–</span>
                        )}
                      </td>
                    );
                  })}
                  <td className="px-3 py-2 text-right font-medium whitespace-nowrap tabular-nums">
                    {money(weighted ? row.totalWeighted : row.totalRaw)}
                  </td>
                </tr>
              );
            })}
            <tr className="bg-muted/20 border-b">
              <th scope="row" className="px-3 py-2 text-left font-normal">
                <span className="font-medium">{t('dealForecast.contracted')}</span>
                <span className="text-muted-foreground mt-0.5 block text-[11px] leading-relaxed">
                  {t('dealForecast.contractedHint')}
                </span>
              </th>
              {forecast.contracted.map((value, index) => (
                <td key={index} className="px-3 py-2 text-right whitespace-nowrap tabular-nums">
                  {money(value)}
                </td>
              ))}
              <td className="px-3 py-2" />
            </tr>
            <tr>
              <Tooltip asChild content={t('dealForecast.expectedHint')}>
                <th scope="row" className="px-3 py-2 text-left font-medium">
                  {t('dealForecast.expected')}
                </th>
              </Tooltip>
              {expected.map((value, index) => (
                <td
                  key={index}
                  className="px-3 py-2 text-right font-medium whitespace-nowrap tabular-nums"
                >
                  {money(value)}
                </td>
              ))}
              <td className="px-3 py-2" />
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Tile({
  label,
  className,
  children,
}: {
  readonly label: string;
  readonly className?: string | undefined;
  readonly children: string;
}) {
  return (
    <div className="bg-card flex min-w-[168px] flex-col gap-0.5 rounded-xl border p-3">
      <span className="text-muted-foreground text-[11px] font-medium tracking-wide uppercase">
        {label}
      </span>
      <span className={cn('text-[22px] leading-tight font-semibold tabular-nums', className)}>
        {children}
      </span>
    </div>
  );
}

function ForecastSkeleton() {
  const t = useT();
  return (
    <div className="min-h-0 flex-1 overflow-auto p-5">
      <div role="status" aria-live="polite" className="sr-only">
        {t('state.loading')}
      </div>
      <div aria-hidden className="flex flex-wrap gap-2">
        {[0, 1, 2, 3].map((index) => (
          <div
            key={index}
            className="bg-card flex min-w-[168px] flex-col gap-2 rounded-xl border p-3"
          >
            <Skeleton width="60%" />
            <Skeleton width="80%" className="h-6" />
          </div>
        ))}
      </div>
      <div aria-hidden className="bg-card mt-3 flex flex-col gap-4 rounded-xl border p-4">
        {[0, 1, 2, 3, 4, 5].map((index) => (
          <Skeleton key={index} width={index % 2 === 0 ? '90%' : '75%'} />
        ))}
      </div>
    </div>
  );
}
